/**
 * TimelineController — Evolución temporal de casos clínicos
 * Scrubber horizontal con pasos, narrativa y re-triage
 */

import { TimelineStep, TimelineState } from './types';
import { TIMELINE_CASES } from './timeline-data';
import { Patient } from '../medical/Patient';
import { TriagePriority } from '../types/medical';
import state from '../core/state';

export class TimelineController {
  private container: HTMLElement | null = null;
  private triagePrompt: HTMLElement | null = null;
  private timelineState: TimelineState | null = null;
  private patient: Patient | null = null;
  private playInterval: number = 0;

  constructor() {
    this.injectStyles();
  }

  start(caseId: string, patient: Patient): void {
    const config = TIMELINE_CASES[caseId];
    if (!config) return;

    this.patient = patient;
    this.timelineState = {
      caseId,
      steps: config.steps,
      currentStepIndex: 0,
      isPlaying: false,
      playSpeed: 5,
      studentAnswers: new Map(),
    };

    state.setEducationMode('timeline');
    this.render();
  }

  stop(): void {
    this.container?.remove();
    this.triagePrompt?.remove();
    this.container = null;
    this.triagePrompt = null;
    this.timelineState = null;
    clearInterval(this.playInterval);
    state.setEducationMode('none');
  }

  private goToStep(index: number): void {
    if (!this.timelineState || !this.patient) return;
    if (index < 0 || index >= this.timelineState.steps.length) return;

    const step = this.timelineState.steps[index];
    this.timelineState.currentStepIndex = index;

    // Aplicar cambios de nodos
    if (step.newNodes) {
      step.newNodes.forEach(nodeData => {
        if (!this.patient!.getNode(nodeData.id)) {
          this.patient!.addNode(nodeData);
          const node = this.patient!.getNode(nodeData.id);
          if (node) {
            node.createLabel();
            node.label!.visible = state.ui.showLabels;
            node.pulse();
          }
        }
      });
    }

    if (step.removedNodeIds) {
      step.removedNodeIds.forEach(id => this.patient!.removeNode(id));
    }

    if (step.changedNodes) {
      step.changedNodes.forEach(change => {
        this.patient!.updateNodeValue(change.nodeId, change.newValue);
        const node = this.patient!.getNode(change.nodeId);
        if (node) node.showTrend(change.trend);
      });
    }

    state.emit({ type: 'TIMELINE_STEP', stepIndex: index, label: step.label });
    this.updateUI();

    // Preguntar re-triage si corresponde
    if (step.askReclassify) {
      setTimeout(() => this.showTriagePrompt(step), 1000);
    }
  }

  private togglePlay(): void {
    if (!this.timelineState) return;

    if (this.timelineState.isPlaying) {
      clearInterval(this.playInterval);
      this.timelineState.isPlaying = false;
    } else {
      this.timelineState.isPlaying = true;
      this.playInterval = window.setInterval(() => {
        const next = this.timelineState!.currentStepIndex + 1;
        if (next >= this.timelineState!.steps.length) {
          clearInterval(this.playInterval);
          this.timelineState!.isPlaying = false;
          this.updateUI();
          return;
        }
        this.goToStep(next);
      }, this.timelineState.playSpeed * 1000);
    }
    this.updateUI();
  }

  private showTriagePrompt(step: TimelineStep): void {
    this.triagePrompt?.remove();
    this.triagePrompt = document.createElement('div');
    this.triagePrompt.className = 'tl-triage-prompt';
    this.triagePrompt.innerHTML = `
      <div class="tl-triage-card">
        <p class="tl-triage-q">¿Cómo reclasificarías al paciente ahora?</p>
        <div class="tl-triage-options">
          <button data-triage="immediate" class="tl-triage-btn tl-triage-red">I — Inmediato</button>
          <button data-triage="delayed" class="tl-triage-btn tl-triage-amber">II — Puede esperar</button>
          <button data-triage="minor" class="tl-triage-btn tl-triage-green">III — Ambulatorio</button>
        </div>
      </div>
    `;
    document.body.appendChild(this.triagePrompt);

    this.triagePrompt.querySelectorAll('.tl-triage-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const answer = (e.target as HTMLElement).dataset.triage as TriagePriority;
        this.handleTriageAnswer(answer, step);
      });
    });
  }

  private handleTriageAnswer(answer: TriagePriority, step: TimelineStep): void {
    if (!this.timelineState) return;

    const isCorrect = answer === step.expectedTriage;
    this.timelineState.studentAnswers.set(this.timelineState.currentStepIndex, answer);

    if (this.triagePrompt) {
      const card = this.triagePrompt.querySelector('.tl-triage-card')!;
      card.innerHTML = `
        <div class="tl-triage-result ${isCorrect ? 'correct' : 'wrong'}">
          <span>${isCorrect ? '&#x2705; Correcto' : '&#x274C; Incorrecto'}</span>
          <p>${isCorrect
            ? 'Tu reclasificación coincide con la evolución del paciente.'
            : `La prioridad esperada era: <strong>${this.getTriageLabel(step.expectedTriage!)}</strong>`
          }</p>
        </div>
      `;
      setTimeout(() => {
        this.triagePrompt?.remove();
        this.triagePrompt = null;
      }, 3000);
    }
  }

  private getTriageLabel(p: TriagePriority): string {
    const labels: Record<TriagePriority, string> = {
      immediate: 'I — Inmediato',
      delayed: 'II — Puede esperar',
      minor: 'III — Ambulatorio',
      expectant: 'IV — Expectante',
      deceased: 'V — Fallecido',
    };
    return labels[p] || p;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // UI
  // ═══════════════════════════════════════════════════════════════════════

  private render(): void {
    if (!this.timelineState) return;

    this.container?.remove();
    this.container = document.createElement('div');
    this.container.className = 'tl-bar';
    document.body.appendChild(this.container);
    this.updateUI();
  }

  private updateUI(): void {
    if (!this.container || !this.timelineState) return;

    const steps = this.timelineState.steps;
    const current = this.timelineState.currentStepIndex;
    const step = steps[current];

    this.container.innerHTML = `
      <div class="tl-narrative">${step.narrative}</div>
      <div class="tl-controls">
        <button class="tl-play-btn" id="tl-play">
          ${this.timelineState.isPlaying ? '&#x23F8;' : '&#x25B6;'}
        </button>
        <div class="tl-track">
          ${steps.map((s, i) => `
            <button class="tl-marker ${i === current ? 'active' : ''} ${i < current ? 'past' : ''}" data-step="${i}">
              <span class="tl-marker-dot"></span>
              <span class="tl-marker-label">${s.label}</span>
            </button>
          `).join('')}
          <div class="tl-track-line">
            <div class="tl-track-fill" style="width: ${(current / Math.max(steps.length - 1, 1)) * 100}%"></div>
          </div>
        </div>
        <button class="tl-close-btn" id="tl-close">&#x2715;</button>
      </div>
    `;

    // Events
    this.container.querySelector('#tl-play')!.addEventListener('click', () => this.togglePlay());
    this.container.querySelector('#tl-close')!.addEventListener('click', () => this.stop());
    this.container.querySelectorAll('.tl-marker').forEach(m => {
      m.addEventListener('click', (e) => {
        const idx = parseInt((e.currentTarget as HTMLElement).dataset.step!);
        this.goToStep(idx);
      });
    });
  }

  private injectStyles(): void {
    if (document.getElementById('tl-styles')) return;
    const s = document.createElement('style');
    s.id = 'tl-styles';
    s.textContent = `
      .tl-bar {
        position: fixed; bottom: 0; left: 0; right: 0;
        z-index: 300; padding: 16px 24px 20px;
        background: rgba(0,0,0,0.8);
        backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
        border-top: 0.5px solid rgba(255,255,255,0.06);
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }
      .tl-narrative {
        font-size: 13px; color: #a1a1a6; line-height: 1.5;
        text-align: center; margin-bottom: 14px; max-width: 600px;
        margin-left: auto; margin-right: auto;
      }
      .tl-controls {
        display: flex; align-items: center; gap: 16px;
      }
      .tl-play-btn, .tl-close-btn {
        width: 36px; height: 36px; border-radius: 50%;
        background: rgba(255,255,255,0.06); border: 0.5px solid rgba(255,255,255,0.1);
        color: #f5f5f7; font-size: 14px; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        transition: all 0.2s; flex-shrink: 0;
      }
      .tl-play-btn:hover, .tl-close-btn:hover { background: rgba(255,255,255,0.12); }
      .tl-track {
        flex: 1; display: flex; align-items: center; justify-content: space-between;
        position: relative; padding: 0 8px;
      }
      .tl-track-line {
        position: absolute; top: 50%; left: 24px; right: 24px;
        height: 2px; background: rgba(255,255,255,0.08);
        transform: translateY(-8px); border-radius: 1px;
        pointer-events: none; z-index: 0;
      }
      .tl-track-fill {
        height: 100%; background: #2997FF; border-radius: 1px;
        transition: width 0.4s cubic-bezier(0.25,1,0.5,1);
      }
      .tl-marker {
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        background: none; border: none; cursor: pointer; padding: 4px 8px;
        position: relative; z-index: 1; transition: all 0.2s;
      }
      .tl-marker-dot {
        width: 12px; height: 12px; border-radius: 50%;
        background: rgba(255,255,255,0.1); border: 2px solid rgba(255,255,255,0.15);
        transition: all 0.3s;
      }
      .tl-marker.active .tl-marker-dot {
        background: #2997FF; border-color: #2997FF;
        box-shadow: 0 0 8px rgba(41,151,255,0.4);
      }
      .tl-marker.past .tl-marker-dot {
        background: rgba(41,151,255,0.4); border-color: rgba(41,151,255,0.5);
      }
      .tl-marker-label {
        font-size: 10px; color: #6e6e73; white-space: nowrap;
        font-family: 'JetBrains Mono', monospace;
      }
      .tl-marker.active .tl-marker-label { color: #f5f5f7; }
      .tl-marker:hover .tl-marker-dot { transform: scale(1.3); }

      .tl-triage-prompt {
        position: fixed; bottom: 120px; right: 24px; z-index: 400;
        animation: tlSlide 0.3s cubic-bezier(0.25,1,0.5,1);
      }
      .tl-triage-card {
        background: rgba(0,0,0,0.9); border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 16px; padding: 20px; min-width: 260px;
        backdrop-filter: blur(16px);
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }
      .tl-triage-q {
        font-size: 14px; font-weight: 600; color: #f5f5f7;
        margin-bottom: 12px;
      }
      .tl-triage-options { display: flex; flex-direction: column; gap: 6px; }
      .tl-triage-btn {
        padding: 10px 16px; border-radius: 10px; border: 0.5px solid;
        font-size: 13px; font-weight: 600; cursor: pointer;
        transition: all 0.2s; text-align: left;
        font-family: inherit;
      }
      .tl-triage-btn:hover { transform: translateX(4px); }
      .tl-triage-red { background: rgba(255,69,58,0.1); border-color: rgba(255,69,58,0.2); color: #FF6961; }
      .tl-triage-amber { background: rgba(255,159,10,0.1); border-color: rgba(255,159,10,0.2); color: #FFB340; }
      .tl-triage-green { background: rgba(48,209,88,0.1); border-color: rgba(48,209,88,0.2); color: #4ADE80; }
      .tl-triage-result {
        text-align: center; padding: 8px;
      }
      .tl-triage-result span { font-size: 15px; font-weight: 600; color: #f5f5f7; }
      .tl-triage-result p { font-size: 12px; color: #a1a1a6; margin-top: 6px; }
      @keyframes tlSlide {
        from { opacity: 0; transform: translateX(16px); }
        to { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(s);
  }
}

export const timelineController = new TimelineController();
export default TimelineController;
